import { Alert, Box, CircularProgress, Container, Typography } from '@mui/material';
import ApiClient from 'Api/ApiClient';
import AuthContext from 'context/AuthContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import classes from 'styles/globalStyle.module.scss';
import { UrlHelpers } from 'urlHelpers/urlHelper';
import { getApiErrorMessage } from 'utils/utility';

import CustomerDetailsPage from 'components/IDMCustomerDetailsPage/CustomerDetailsPage';

import SearchFilterForm from './SearchFilterForm';
import SearchResultsTable from './SearchResultsTable';

function CustomerSearchPage() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailsError, setDetailsError] = useState('');
  const [form, setForm] = useState({
    username: '',
    email: '',
    contactNumber: '',
    contactName: '',
    businessName: '',
  });

  const apiClient = new ApiClient();
  const { accessToken, setInitiateUiResponse } = useContext(AuthContext);
  useEffect(() => {
    const initiateUI = async () => {
      try {
        const data = await apiClient.get(
          UrlHelpers.authInitiateui,
          {
            headers: {
              appid: 'notifyadmin',
            },
          },
          accessToken
        );

        setInitiateUiResponse(data);
      } catch (error) {
        console.error('Initiate UI Error:', error);
      }
    };

    if (accessToken) {
      initiateUI();
    }
  }, [accessToken]);

  const emailError = useMemo(() => {
    if (!form.email) return '';
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(form.email) ? '' : 'Invalid email format';
  }, [form.email]);
  const handleChange = (field, value) => {
    setForm((prev) => {
      const updatedForm = { ...prev };
      updatedForm[field] = value;
      return updatedForm;
    });
  };

  const handleClear = () => {
    setForm({
      username: '',
      email: '',
      contactNumber: '',
      contactName: '',
      businessName: '',
    });
    setResults([]);
    setSearched(false);
    setDetailsError('');
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setDetailsError('');

      const params = new URLSearchParams();

      if (form.username) params.append('username', form.username);
      if (form.email) params.append('email', form.email);
      if (form.contactNumber) params.append('contactNumber', form.contactNumber);
      if (form.contactName) params.append('contactName', form.contactName);
      if (form.businessName) params.append('businessName', form.businessName);

      const response = await fetch(UrlHelpers.userSearch(params.toString()), {
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : '',
          appid: 'notifyadmin',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      setResults(data.matches || []);
      setSearched(true);
    } catch (error) {
      console.error('Customer search error:', error);
      setResults([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (user) => {
    try {
      setLoading(true);
      setDetailsError('');
      const detailsResponse = await fetch(
        UrlHelpers.userDetails(`username=${user.username}&registeredfrom=${user.registeredfrom}`),
        {
          headers: {
            Authorization: accessToken ? `Bearer ${accessToken}` : '',
            appid: 'notifyadmin',
          },
        }
      );

      const detailsData = await detailsResponse.json();

      const customerDetails = detailsData?.customerDetails;
      const hasDetails =
        detailsResponse.ok && customerDetails && Object.keys(customerDetails).length > 0;

      if (!hasDetails) {
        setSelectedCustomer(null);
        const apiMessage = getApiErrorMessage(detailsData);
        setDetailsError(apiMessage ? `No details found: ${apiMessage}` : 'No details found.');
        return;
      }

      const auditResponse = await fetch(
        UrlHelpers.userAudit(`username=${user.username}&registeredfrom=${user.registeredfrom}`),
        {
          headers: {
            Authorization: accessToken ? `Bearer ${accessToken}` : '',
            appid: 'notifyadmin',
          },
        }
      );

      const auditData = await auditResponse.json();

      setSelectedCustomer({
        ...customerDetails,
        auditTrail: auditData.auditTrail || [],
      });
    } catch (error) {
      console.error(error);
      setSelectedCustomer(null);
      setDetailsError('No details found.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToResults = () => {
    setSelectedCustomer(null);
  };

  if (selectedCustomer) {
    return <CustomerDetailsPage customer={selectedCustomer} onBack={handleBackToResults} />;
  }

  return (
    <Container maxWidth="xl" className={classes.adminPortal} sx={{ px: { xs: 0, sm: 2, md: 3 } }}>
      <Box sx={{ py: { xs: 2, sm: 3, md: 5 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={{ xs: 3, md: 5 }}
        >
          <Box>
            <Typography
              sx={{
                color: '#000000',
                fontSize: {
                  xs: '24px !important',
                  sm: '30px !important',
                  md: '36px !important',
                },
                lineHeight: 1.2,
                fontWeight: 700,
              }}
            >
              Find a customer to assist.
            </Typography>
          </Box>
        </Box>
        <SearchFilterForm
          values={form}
          errors={{
            email: emailError,
          }}
          loading={loading}
          onChange={handleChange}
          onClear={handleClear}
          onSearch={handleSearch}
        />
        {loading && (
          <Box display="flex" justifyContent="center" mt={{ xs: 3, md: 5 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && searched && results.length === 0 && (
          <Alert
            severity="info"
            sx={{
              mt: { xs: 3, md: 4 },
              px: { xs: 0, sm: 2 },
              bgcolor: 'transparent',
              backgroundColor: 'transparent',
              color: '#d71714',
              fontSize: { xs: '16px !important', md: '20px !important' },
              border: 'none',
              '& .MuiAlert-icon': {
                color: '#d71714',
              },
              '& .MuiAlert-message': {
                color: '#d71714',
              },
            }}
          >
            No records were found. Please refine the search criteria
          </Alert>
        )}
        {!loading && detailsError && (
          <Alert
            severity="info"
            sx={{
              mt: { xs: 3, md: 4 },
              px: { xs: 0, sm: 2 },
              bgcolor: 'transparent',
              backgroundColor: 'transparent',
              color: '#d71714',
              fontSize: { xs: '16px !important', md: '20px !important' },
              border: 'none',
              '& .MuiAlert-icon': {
                color: '#d71714',
              },
              '& .MuiAlert-message': {
                color: '#d71714',
              },
            }}
          >
            {detailsError}
          </Alert>
        )}
        {!loading && results.length > 0 && (
          <SearchResultsTable users={results} onRowClick={handleUserSelect} />
        )}
      </Box>
    </Container>
  );
}
export default CustomerSearchPage;
